import {EquipmentTypedModel} from 'procedures/ProcedureDomainTypes';
import {
  FieldType,
  RequiredLink,
  RequiredLinkCustomSchemaInterface,
  RequiredLinkFolderItemType,
  RequiredLinkTagSchema,
} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';

export interface MaterialType {
  type: FieldType.TEXT | FieldType.BIOENTITY_LINK;
  requiredLink: RequiredLink | null;
  unit: null;
  schemaFieldSelectorId?: never;
}

export interface MaterialOutputType {
  type: FieldType.BIOENTITY_LINK;
  requiredLink:
    | RequiredLinkTagSchema
    | (RequiredLinkFolderItemType & {folderItemType: string})
    | RequiredLinkCustomSchemaInterface;
  unit: null;
  schemaFieldSelectorId?: never;
}

export interface EquipmentType {
  type: FieldType.TEXT | FieldType.BIOENTITY_LINK;
  requiredLink: RequiredLinkTagSchema | RequiredLinkCustomSchemaInterface | null;
  unit: null;
  schemaFieldSelectorId?: never;
}

export interface UnitSummary {
  id: string;
  name: string;
  symbol: string;
}
interface DecimalDataParameterType {
  type: FieldType.FLOAT;
  requiredLink: null;
  unit: UnitSummary | null;
  schemaFieldSelectorId?: never;
}
interface TextDataParameterType {
  type: FieldType.TEXT;
  requiredLink: null;
  unit: null;
  schemaFieldSelectorId?: never;
}
interface DatetimeDataParameterType {
  type: FieldType.DATETIME;
  requiredLink: null;
  unit: null;
  schemaFieldSelectorId?: never;
}
export interface DropdownDataParameterType {
  type: FieldType.SELECTOR;
  requiredLink: null;
  unit: null;
  schemaFieldSelectorId: string;
}

export type DataParameterType =
  | DecimalDataParameterType
  | TextDataParameterType
  | DatetimeDataParameterType
  | DropdownDataParameterType;
export type MaterialInputType = MaterialType;
export type EquipmentInputType = EquipmentType;
export type ProcedureSchemaFieldType = MaterialType | DataParameterType;

export const TEXT_FIELD_TYPE: TextDataParameterType = {
  type: FieldType.TEXT as FieldType.TEXT,
  requiredLink: null,
  unit: null,
};
export const DECIMAL_FIELD_TYPE: DecimalDataParameterType = {
  type: FieldType.FLOAT as FieldType.FLOAT,
  requiredLink: null,
  unit: null,
};
export const DATETIME_FIELD_TYPE: DatetimeDataParameterType = {
  type: FieldType.DATETIME as FieldType.DATETIME,
  requiredLink: null,
  unit: null,
};
export const BIOENTITY_LINK_FIELD_TYPE = {
  type: FieldType.BIOENTITY_LINK as FieldType.BIOENTITY_LINK,
  requiredLink: {
    tagSchema: null,
    entitySchemaFieldPath: null,
    folderItemType: null,
    schemaInterface: null,
  },
  unit: null,
};

export const DEFAULT_MATERIAL_TYPE: MaterialType = TEXT_FIELD_TYPE;
export const DEFAULT_MATERIAL_INPUT_TYPE: MaterialInputType = TEXT_FIELD_TYPE;

/**
 *  Note that material outputs do not have a truly usable default value. The user is required to
 *  select a specific schema, and there's no way for us to select a meaningful and non-misleading
 *  default.
 *
 *  So this "starter" value is something that can/should only be used in the front end during
 *  draft-state editing. Saving this value will be blocked by our validation tools (see
 *  `validateMaterialOutputSettingsHaveSchemaSelected`)
 */
export const STARTER_MATERIAL_OUTPUT_TYPE: MaterialOutputType = {
  type: FieldType.BIOENTITY_LINK as FieldType.BIOENTITY_LINK,
  requiredLink: {
    tagSchema: null,
    entitySchemaFieldPath: null,
    folderItemType: null,
    schemaInterface: null,
  },
  unit: null,
};
export const DEFAULT_DATA_PARAMETER_TYPE: DataParameterType = DECIMAL_FIELD_TYPE;
export const DEFAULT_EQUIPMENT_TYPE: EquipmentType = TEXT_FIELD_TYPE;
export const DEFAULT_EQUIPMENT_MODEL: EquipmentTypedModel = {type: FieldType.TEXT, value: null};

export const SUPPORTED_DATA_PARAMETER_FIELD_TYPES = [
  FieldType.TEXT,
  FieldType.FLOAT,
  FieldType.DATETIME,
  FieldType.SELECTOR,
] as const;

export type DataParameterFieldType = (typeof SUPPORTED_DATA_PARAMETER_FIELD_TYPES)[number];

export function isDataParameterFieldType(type: unknown): type is DataParameterFieldType {
  return (
    typeof type === 'string' && (SUPPORTED_DATA_PARAMETER_FIELD_TYPES as ReadonlyArray<string>).includes(type)
  );
}

export function isDropdownDataParameterType(
  dataParameterType: DataParameterType
): dataParameterType is DropdownDataParameterType {
  return dataParameterType.type === FieldType.SELECTOR;
}

export function getSchemaFieldSelectorsById(
  schemaFieldSelectors: Array<SchemaFieldSelector>
): Record<string, SchemaFieldSelector> {
  const selectorsById: Record<string, SchemaFieldSelector> = {};
  schemaFieldSelectors.forEach((selector) => {
    selectorsById[selector.id] = selector;
  });
  return selectorsById;
}
